import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMyProfile } from '../hooks/useMyProfile';
import { useTripBillingOverview, type TripBillingOverview } from '../hooks/useTripBilling';
import {
  BillingOutcomeControls,
  BillingStatusPill,
  BillingTermsForm,
  TripStatusActions,
  formatEstimate,
  formatHours,
} from '../components/trips/TripControls';
import { formatCurrency, formatDate } from '../lib/formatters';
import {
  BASIS_LABELS,
  QUEUE_BUCKETS,
  billingWarnings,
  canRecordInvoice,
  canSetBillingTerms,
  queueBucket,
  toNumber,
  type QueueBucket,
} from '../lib/tripLifecycle';

/**
 * The billing department's queue. Every trip lands in exactly one bucket
 * (queueBucket), so the counts always add up to the number of trips.
 *
 * Nothing on this page writes to Zoho. Billing invoices in Zoho Books and records
 * the invoice number here; the Books sync can match on it later.
 */
export function TripBillingPage() {
  const { data, isLoading, isError } = useTripBillingOverview();
  const { data: me } = useMyProfile();
  const [params, setParams] = useSearchParams();
  const bucketParam = params.get('view') as QueueBucket | null;
  const [query, setQuery] = useState('');

  const rows = useMemo(() => data ?? [], [data]);
  const counts = useMemo(() => {
    const c = Object.fromEntries(QUEUE_BUCKETS.map((b) => [b.key, 0])) as Record<QueueBucket, number>;
    for (const r of rows) c[queueBucket(r)] += 1;
    return c;
  }, [rows]);

  // Default to the first bucket with work in it, in queue order.
  const defaultBucket = (['review', 'terms', 'ready'] as QueueBucket[]).find((b) => counts[b] > 0) ?? 'ready';
  const bucket: QueueBucket = bucketParam && QUEUE_BUCKETS.some((b) => b.key === bucketParam) ? bucketParam : defaultBucket;
  const meta = QUEUE_BUCKETS.find((b) => b.key === bucket)!;

  const q = query.trim().toLowerCase();
  const visible = rows
    .filter((r) => queueBucket(r) === bucket)
    .filter(
      (r) =>
        !q ||
        [r.project_number, r.customer_name, r.location_name, r.po_number, r.invoice_reference, r.lead_technician_name]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q)),
    );

  const readyTotal = rows
    .filter((r) => queueBucket(r) === 'ready')
    .reduce((sum, r) => sum + (toNumber(r.estimated_amount_cents) ?? 0), 0);

  const choose = (key: QueueBucket) => {
    const next = new URLSearchParams(params);
    next.set('view', key);
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-text">Trip Billing</h1>
          <p className="text-sm text-text-muted mt-0.5">
            Completed trips move here on their own. Invoice them in Zoho Books, then record the invoice number.
          </p>
        </div>
        {counts.ready > 0 && (
          <p className="text-sm text-text-muted">
            Ready to bill: <span className="font-semibold text-text">{counts.ready}</span>
            {readyTotal > 0 && <> · estimated <span className="font-semibold text-text">{formatCurrency(readyTotal)}</span></>}
          </p>
        )}
      </div>

      <div className="flex gap-1 overflow-x-auto max-w-full" role="tablist" aria-label="Billing queue">
        {QUEUE_BUCKETS.map((b) => (
          <button
            key={b.key}
            type="button"
            role="tab"
            aria-selected={bucket === b.key}
            onClick={() => choose(b.key)}
            className={`px-3 py-1.5 min-h-[44px] sm:min-h-0 text-xs font-medium rounded-md whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              bucket === b.key ? 'bg-primary text-text-inverse' : 'bg-surface-3 text-text-muted hover:bg-surface-strong'
            }`}
          >
            {b.label}
            <span
              className={`ml-1.5 rounded-full px-1.5 ${
                bucket === b.key ? 'bg-surface/20' : (b.key === 'review' || b.key === 'terms') && counts[b.key] > 0 ? 'bg-warning-soft text-warning' : 'bg-surface'
              }`}
            >
              {isLoading ? '…' : counts[b.key]}
            </span>
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="text-sm text-text-muted">{meta.hint}</p>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search project, customer, PO, invoice…"
          aria-label="Search trips"
          className="w-full sm:w-72 rounded border border-border bg-surface px-3 py-1.5 min-h-[40px] text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {isLoading ? (
        <div className="rounded-lg border border-border bg-surface p-6 text-sm text-text-muted" role="status">Loading trips…</div>
      ) : isError ? (
        <div role="alert" className="rounded-lg border border-border bg-surface p-6 text-sm text-notification">
          Could not load the billing queue. Refresh to try again.
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-text-muted">
          {q ? 'No trips match this search.' : 'Nothing here right now.'}
        </div>
      ) : (
        <ul className="space-y-2">
          {visible.map((r) => (
            <QueueRow
              key={r.trip_id}
              row={r}
              bucket={bucket}
              mayEditTerms={canSetBillingTerms(me?.role)}
              mayRecord={canRecordInvoice(me?.role)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function QueueRow({
  row,
  bucket,
  mayEditTerms,
  mayRecord,
}: {
  row: TripBillingOverview;
  bucket: QueueBucket;
  mayEditTerms: boolean;
  mayRecord: boolean;
}) {
  const [editingTerms, setEditingTerms] = useState(false);
  const warnings = bucket === 'ready' ? billingWarnings(row) : [];

  return (
    <li className="rounded-lg border border-border bg-surface p-3 sm:p-4 space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/trips?trip=${row.trip_id}`} className="font-mono text-sm font-medium text-primary hover:underline">
              {row.project_number}
            </Link>
            <BillingStatusPill status={row.billing_status} />
            {row.is_overdue && <span className="text-xs font-medium text-warning">Overdue</span>}
          </div>
          <p className="text-sm text-text truncate">
            {row.customer_name ?? 'Unknown customer'} · {row.location_name ?? 'No location'}
          </p>
          <p className="text-xs text-text-muted">
            {row.start_date ? formatDate(row.start_date) : 'No start date'}
            {row.end_date && row.end_date !== row.start_date ? ` → ${formatDate(row.end_date)}` : ''}
            {row.lead_technician_name ? ` · ${row.lead_technician_name}` : ''}
            {row.po_number ? ` · PO ${row.po_number}` : ''}
          </p>
        </div>
        <dl className="grid grid-cols-3 gap-3 text-right text-xs shrink-0">
          <div>
            <dt className="text-text-faint">Basis</dt>
            <dd className="text-text">{row.billing_basis ? BASIS_LABELS[row.billing_basis] : <span className="text-warning">Not set</span>}</dd>
          </div>
          <div>
            <dt className="text-text-faint">Hours</dt>
            <dd className="text-text">{formatHours(row.labor_hours)}</dd>
          </div>
          <div>
            <dt className="text-text-faint">{row.billing_status === 'invoiced' ? 'Invoice' : 'Estimate'}</dt>
            <dd className="text-text font-medium">
              {row.billing_status === 'invoiced' ? <span className="font-mono">{row.invoice_reference}</span> : formatEstimate(row)}
            </dd>
          </div>
        </dl>
      </div>

      {warnings.length > 0 && (
        <ul className="rounded border border-warning bg-warning-soft px-3 py-2 text-xs text-warning space-y-0.5">
          {warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}

      {bucket === 'review' && (
        <div className="space-y-1">
          <p className="text-xs text-text-muted">
            {row.report_count > 0
              ? `${row.submitted_report_count} daily report${row.submitted_report_count === 1 ? '' : 's'} submitted, last on ${formatDate(row.last_report_date)}.`
              : 'No daily reports filed.'}{' '}
            To move the dates, open the trip.
          </p>
          <TripStatusActions tripId={row.trip_id} status={row.trip_status} billingStatus={row.billing_status} />
        </div>
      )}

      {(bucket === 'terms' || bucket === 'ready') && mayEditTerms && (
        editingTerms ? (
          <BillingTermsForm row={row} onDone={() => setEditingTerms(false)} />
        ) : (
          <button type="button" onClick={() => setEditingTerms(true)} className="text-xs font-medium text-primary hover:underline min-h-[32px]">
            {row.billing_basis ? 'Change billing terms' : 'Set billing terms'}
          </button>
        )
      )}

      {mayRecord && (bucket === 'ready' || bucket === 'history' || bucket === 'invoiced' || (bucket === 'not_billable' && row.trip_status === 'completed')) && (
        <BillingOutcomeControls row={row} compact />
      )}
    </li>
  );
}
